import type { SchedulerTypeKey } from '@jarvis/platform-ui';
import { groupSchedulerEventsByDate, formatAgendaEventRange } from '../timeOffCalendarAgenda';
import { appointmentLifecycleClassName } from '../timeOffCalendarTypes';
import type { TimeOffSchedulerEvent } from '../timeOffCalendarTypes';

interface TypeColor {
  id: SchedulerTypeKey;
  color: string;
}

export interface TimeOffCalendarAgendaListProps {
  events: TimeOffSchedulerEvent[];
  typeColors: TypeColor[];
  onSelectEvent: (event: TimeOffSchedulerEvent) => void;
  /** Mobile layout: section header above the scrollable list. */
  compact?: boolean;
  monthLabel?: string;
}

export function TimeOffCalendarAgendaList({
  events,
  typeColors,
  onSelectEvent,
  compact = false,
  monthLabel
}: TimeOffCalendarAgendaListProps) {
  const groups = groupSchedulerEventsByDate(events);

  if (groups.length === 0) {
    return (
      <aside className={`time-off-calendar-agenda${compact ? ' time-off-calendar-agenda--compact' : ''}`} aria-label="Time off agenda">
        {compact && monthLabel ? (
          <div className="time-off-calendar-agenda__header" role="heading" aria-level={2}>
            Time off <span className="time-off-calendar-agenda__header-month">{monthLabel}</span>
          </div>
        ) : null}
        <p className="time-off-calendar-agenda__empty">No time off in this period.</p>
      </aside>
    );
  }

  return (
    <aside className={`time-off-calendar-agenda${compact ? ' time-off-calendar-agenda--compact' : ''}`} aria-label="Time off agenda">
      {compact && monthLabel ? (
        <div className="time-off-calendar-agenda__header" role="heading" aria-level={2}>
          Time off <span className="time-off-calendar-agenda__header-month">{monthLabel}</span>
        </div>
      ) : null}
      {groups.map((group) => (
        <section key={group.dateKey} className="time-off-calendar-agenda__day">
          <div className="time-off-calendar-agenda__day-label" role="heading" aria-level={3}>
            {group.label}
          </div>
          <ul className="time-off-calendar-agenda__list">
            {group.events.map((event) => {
              const typeColor =
                typeColors.find((t) => t.id === event.type)?.color ?? 'var(--ops-color-muted, #64748b)';
              const statusClass = appointmentLifecycleClassName(event.status, event.ended);
              const cancelled = event.status === 'Cancelled';
              return (
                <li key={`${group.dateKey}-${event.id}`}>
                  <button
                    type="button"
                    className={`time-off-calendar-agenda__item ${statusClass}`}
                    onClick={() => onSelectEvent(event)}
                  >
                    <span
                      className="time-off-calendar-agenda__swatch"
                      style={{ backgroundColor: typeColor }}
                      aria-hidden
                    />
                    {cancelled ? (
                      <span className="time-off-appt__status-dot time-off-appt__status-dot--cancelled" aria-hidden />
                    ) : null}
                    <span className="time-off-calendar-agenda__item-body">
                      <span className="time-off-calendar-agenda__item-title">{event.text}</span>
                      <span className="time-off-calendar-agenda__item-meta">{formatAgendaEventRange(event)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </aside>
  );
}
